import { ReactElement, useId } from 'react';

interface Props {
  data: number[];
  className?: string | undefined;
  color?: string | undefined;
  strokeWidth?: number | undefined;
}

const VIEW_W = 100;
const VIEW_H = 36;
const PAD_Y = 4;

/**
 * Builds a smooth (Catmull-Rom → cubic bezier) path through the points so the
 * sparkline curves like the reference design rather than showing hard corners.
 */
const buildSmoothPath = (points: Array<{ x: number; y: number }>): string => {
  const first = points[0];
  if (!first || points.length < 2) return '';

  let d = `M ${first.x.toFixed(2)} ${first.y.toFixed(2)}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    if (!p1 || !p2) continue;

    const p0 = points[i - 1] ?? p1;
    const p3 = points[i + 2] ?? p2;

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }

  return d;
};

/**
 * Compact inline trend line (no axes/labels) used alongside metric values.
 * Renders a smoothed line with a fading area fill beneath it, stretching to
 * fill its container; the stroke stays a constant width thanks to
 * vector-effect: non-scaling-stroke.
 */
export const Sparkline = ({
  data,
  className,
  color = '#3eb077',
  strokeWidth = 2,
}: Props): ReactElement | null => {
  const gradientId = `sparkline-${useId().replace(/:/g, '')}`;

  if (data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((value, i) => ({
    x: (i / (data.length - 1)) * VIEW_W,
    y: VIEW_H - PAD_Y - ((value - min) / range) * (VIEW_H - PAD_Y * 2),
  }));

  const linePath = buildSmoothPath(points);
  const areaPath = `${linePath} L ${VIEW_W} ${VIEW_H} L 0 ${VIEW_H} Z`;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      fill="none"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
      <path
        d={linePath}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        style={{ filter: `drop-shadow(0 0 3px ${color}59)` }}
      />
    </svg>
  );
};
